"use client";

import { useEffect, useRef } from "react";
const noop = () => {};

/**
 * Applies the `onClickOutside` and `onClickInside` event handlers to the provided children
 * @param {object} options - The options object
 * @param {(event: MouseEvent) => void} [options.onClickInside] - Handler for clicks inside the referenced element
 * @param {(event: MouseEvent) => void} [options.onClickOutside] - Handler for clicks outside the referenced element
 * @returns {React.RefObject<HTMLElement>} A ref object to be attached to the target element
 */
export const useClickBoundary = ({
  onClickInside = noop,
  onClickOutside = noop
} = {}) => {
  const clickRef = useRef(null);
  useEffect(() => {
    const handleGlobalBodyClick = event => {
      if (clickRef.current) {
        clickRef.current.contains(event.target) ? onClickInside(event) : onClickOutside(event);
      }
    };
    window.document.body.addEventListener("click", handleGlobalBodyClick, {
      capture: true
    });
    return () => window.document.body.removeEventListener("click", handleGlobalBodyClick, {
      capture: true
    });
  }, [onClickInside, onClickOutside]);
  return clickRef;
};
useClickBoundary.displayName = "useClickBoundary";

/**
 * Detect if a click event happens inside or outside of a specified element.
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - Called on render with one argument: refHandler
 * @param {Function} [props.onClickOutside] - Called when window catches a click whose target is NOT a descendant of ref
 * @param {Function} [props.onClickInside] - Called when window catches a click whose target is a descendant of ref
 * @returns {React.ReactNode} The ClickBoundary element
 */
const ClickBoundary = ({
  children,
  onClickOutside,
  onClickInside
}) => {
  const clickRef = useClickBoundary({
    onClickInside,
    onClickOutside
  });
  return children(clickRef);
};
ClickBoundary.displayName = "ClickBoundary";
export default ClickBoundary;